import React, { createContext, useState, useContext, ReactNode, useCallback, useRef } from 'react';
import { Customer, Site } from '../types/network';
import { fetchCustomers, fetchCustomerSites } from '../services/api';

interface SiteContextType {
  customer: Customer | null;
  site: Site | null;
  setCustomer: (customer: Customer | null) => void;
  setSite: (site: Site | null) => void;
  loadSiteAndCustomer: (siteId: string, customerId: string) => Promise<void>;
}

const SiteContext = createContext<SiteContextType | undefined>(undefined);

export const SiteProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [site, setSite] = useState<Site | null>(null);
  const siteRef = useRef(site);
  const customerRef = useRef(customer);

  siteRef.current = site;
  customerRef.current = customer;

  const loadSiteAndCustomer = useCallback(async (siteId: string, customerId: string) => {
    if (siteRef.current && customerRef.current && siteRef.current.ID === siteId && customerRef.current.ID === customerId) {
      return;
    }

    try {
      const sites = await fetchCustomerSites(customerId);
      const currentSite = sites.find(s => s.ID === siteId);
      if (currentSite) {
        setSite(currentSite);
        if (currentSite.CustomerID) {
          const customers = await fetchCustomers();
          const currentCustomer = customers.find(c => c.ID === currentSite.CustomerID);
          setCustomer(currentCustomer || null);
        }
      }
    } catch (error) {
      console.error("Failed to load site and customer data", error);
    }
  }, []);

  return (
    <SiteContext.Provider value={{ customer, site, setCustomer, setSite, loadSiteAndCustomer }}>
      {children}
    </SiteContext.Provider>
  );
};

export const useSite = () => {
  const context = useContext(SiteContext);
  if (context === undefined) {
    throw new Error('useSite must be used within a SiteProvider');
  }
  return context;
};
